import { Alert, Box, LinearProgress, Link, TextField, Typography } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Button } from '../../../../components/ui';
import FileInput from '../../../../components/Shared/FileUploadPreview/FileInput.jsx';
import useTemplateSx from '../../hooks/useTemplateSx';
import { spacingFunction } from '../../../../theme/tokens';
export default function TemplateSendBulkPanel({ send }) {
  const styles = useTemplateSx();

  return (
    <Box sx={styles.sendDialogSection}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <UploadFileOutlinedIcon fontSize="small" color="primary" />
        <Typography sx={styles.sendDialogSectionTitle}>Bulk Send via Excel</Typography>
      </Box>
      <Typography sx={styles.sendDialogSectionHint}>
        Upload an Excel file with recipient phone numbers and template variables. Download the
        sample file to see the required format.
      </Typography>

      <TextField
        type="datetime-local"
        size="small"
        label="Schedule send (optional)"
        value={send.scheduledAt}
        onChange={(e) => send.setScheduledAt(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ mt: 1.5, mb: 1, width: '100%', maxWidth: 320 , 
        '& .MuiOutlinedInput-notchedOutline': {
          top: spacingFunction(0.125),
        },}}
      />

      {send.bulkUploadError ? (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          {send.bulkUploadError}
        </Alert>
      ) : null}

      <Box sx={styles.sendDialogBulkRow}>
        <FileInput
          onFileSelect={send.onBulkFileSelect}
          onFileRemove={send.onBulkFileRemove}
          file={send.tempDataFile}
          setFile={send.setTempDataFile}
          accept=".xlsx,.xls"
        />
        {send.tempDataFile ? (
          <Button
            variant="primary"
            size="sm"
            loading={send.isUploadLoading}
            disabled={send.isUploadLoading}
            onClick={send.uploadBulkFile}
          >
            Upload
          </Button>
        ) : null}
      </Box>

      {send.progressBar > 0 ? (
        <Box pt={2}>
          <LinearProgress
            variant="determinate"
            value={send.progressBar}
            sx={{
              '& .MuiLinearProgress-bar': {
                bgcolor: send.isValidFile ? 'primary.main' : 'error.main',
              },
            }}
          />
          <Typography variant="caption" display="block" textAlign="center" mt={0.5}>
            {`${send.progressBar}%`}
          </Typography>
        </Box>
      ) : null}

      {send.queueStatus ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Queue: {send.queueStatus.pending ?? 0} ready, {send.queueStatus.scheduled ?? 0} scheduled,{' '}
          {send.queueStatus.failed ?? 0} failed
        </Alert>
      ) : null}

      {send.userCount > 0 ? (
        <Box sx={styles.sendDialogStatRow}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            Users ready to send: <strong>{send.userCount}</strong>
            {send.queueCount > 0 ? (
              <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                ({send.queueCount} in queue)
              </Typography>
            ) : null}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {send.queueStatus?.failed > 0 ? (
              <Button
                variant="outline"
                size="sm"
                loading={send.isRetryLoading}
                disabled={send.isRetryLoading}
                onClick={send.retryFailedBulk}
              >
                Retry Failed
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              loading={send.isClearQueueLoading}
              disabled={send.isClearQueueLoading || send.isSendLoading}
              onClick={send.clearBulkQueue}
            >
              Remove Queue
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={send.isSendLoading}
              disabled={send.userCount === 0 || send.isSendLoading || send.isClearQueueLoading}
              onClick={send.sendBulkCron}
            >
              Queue Bulk Send
            </Button>
          </Box>
        </Box>
      ) : null}

      {send.isSendLoading && send.queueCount > 0 ? (
        <Typography variant="body2" mt={1.5} color="success.main">
          {`Successfully queued ${send.queueCount} templates for sending.`}
        </Typography>
      ) : null}

      <Link
        component="button"
        variant="body2"
        onClick={send.downloadSampleFile}
        sx={{ mt: 2, display: 'inline-block' }}
      >
        Download sample file
      </Link>
    </Box>
  );
}
